import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { apiV2Instance } from '../../common/lib/api-v2.client';
import { CreditScope, useLifetimeCredits } from '../lifetime-credits/hooks/useLifetimeCredits';
import { SendGiftFields } from '../lifetime-credits/send-gift-fields.component';
import FieldTip from './field-tip';
import { tipSlotProps } from './tip-font';
import {
  expiryChoices,
  expirySelectionFits,
  selectedExpireChoice,
  sendDrawPreview,
} from '../lifetime-credits/gift-sent';
import { giftCountWithinInventory } from '../lifetime-credits/gift-invite-welcome';

type ScopeView = {
  id: string;
  count: number;
  available?: number;
  allocated?: number;
  redeemed?: number;
  displayedReserved?: number;
  requested?: number | null;
  requestedState?: 'ready' | 'unavailable';
  title: string;
  expiresAt: string | null;
} | null;

type Offer = {
  dual: ScopeView;
  single: ScopeView;
};

function submissionKey(scope: CreditScope): string {
  return `qc_ql_add_${scope}`;
}

function submissionId(scope: CreditScope): string {
  const existing = window.localStorage.getItem(submissionKey(scope));
  if (existing) return existing;
  const created = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.localStorage.setItem(submissionKey(scope), created);
  return created;
}

function readError(error: unknown): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })
    ?.response?.data?.message;
  if (Array.isArray(message)) return message.join(' ');
  return typeof message === 'string' && message ? message : 'Those gift cards could not be added.';
}

const CAMPAIGN_TITLE = 'QuickLinks Gift Card Campaign';

function expiryText(value: string | null): string {
  if (!value) return 'No expiry';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No expiry';
  return date.toLocaleDateString();
}

export function useQuickLinksOffer(opener = 0, onAdded?: () => void): {
  singleCount: number;
  dualCount: number;
  single: ScopeView;
  dual: ScopeView;
  total: number;
  open: () => void;
  openWith: (next: CreditScope) => void;
  dialog: JSX.Element;
} {
  const history = useHistory();
  const added = useRef(onAdded);
  added.current = onAdded;
  const { dashboard, refresh } = useLifetimeCredits();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<CreditScope>('SINGLE');
  const [count, setCount] = useState(1);
  const [expire, setExpire] = useState('3');
  const [customDays, setCustomDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    apiV2Instance
      .get('/api/lifetime-access-credits/quick-links-offer')
      .then((response) => setOffer(response.data))
      .catch(() => setOffer({ dual: null, single: null }));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (opener > 0) setOpen(true);
  }, [opener]);

  const view = scope === 'DUAL' ? offer?.dual : offer?.single;
  const creating = !view;
  const stock = {
    selfPurchased: scope === 'DUAL'
      ? dashboard?.inventory.dualAvailable ?? 0
      : dashboard?.inventory.singleAvailable ?? 0,
    complementary: scope === 'DUAL'
      ? dashboard?.inventory.dualComplementary ?? 0
      : dashboard?.inventory.singleComplementary ?? 0,
    complementaryDaysLeft: scope === 'DUAL'
      ? dashboard?.inventory.complementaryDualDaysLeft ?? null
      : dashboard?.inventory.complementarySingleDaysLeft ?? null,
    holdings: (dashboard?.holdings || [])
      .filter((row) => row.scope === scope && row.countLeft > 0)
      .map((row) => ({
        countLeft: row.countLeft,
        daysLeft: row.daysLeft,
        createdAt: row.createdAt || null,
      })),
  };
  const preview = sendDrawPreview(stock, count);
  const expireOptions = expiryChoices(preview.daysLeft, preview.allowNever);
  const expireValue = selectedExpireChoice(expireOptions, expire);
  const countOk = giftCountWithinInventory(count, preview.available);
  const expiryOk = !creating || expirySelectionFits(expireValue, customDays, preview.daysLeft, preview.allowNever);
  const showPassFields = creating;
  const outOfStock = preview.available === 0;
  const addBlocked = busy || outOfStock || !countOk || !expiryOk;
  const total = (offer?.dual?.count || 0) + (offer?.single?.count || 0);
  const emptyMessage = scope === 'DUAL'
    ? 'You do not have any Dual LA Pass Gift Cards. Buy more now!'
    : 'You do not have any Single LA Pass Gift Cards. Buy more now!';
  const overLimit = count > preview.available;
  let amountHelp: string | undefined;
  if (outOfStock) amountHelp = emptyMessage;
  else if (overLimit) amountHelp = `You only have ${preview.available} gift cards to give.`;
  else if (preview.available > 0) amountHelp = `You can send up to ${preview.available}.`;

  useEffect(() => {
    if (preview.available === 0) {
      setCount(0);
      return;
    }
    setCount((current) => (current < 1 ? 1 : current));
  }, [scope, preview.available]);

  const save = () => {
    setBusy(true);
    setError('');
    apiV2Instance
      .post('/api/lifetime-access-credits/quick-links-offer/add', {
        scope,
        count,
        submissionId: submissionId(scope),
        title: creating ? CAMPAIGN_TITLE : undefined,
        expirationPreset: creating ? expireValue : undefined,
        customDays: creating && expireValue === 'custom' ? customDays : undefined,
      })
      .then(async (response) => {
        window.localStorage.removeItem(submissionKey(scope));
        setOffer(response.data);
        await refresh();
        setOpen(false);
        setCount(1);
        if (added.current) added.current();
      })
      .catch((saveError) => {
        setError(readError(saveError));
      })
      .finally(() => setBusy(false));
  };

  const openDialog = () => setOpen(true);
  const openWith = (next: CreditScope) => {
    setScope(next);
    setOpen(true);
  };

  const dialog = (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      sx={{ '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiMenuItem-root': { fontSize: { xs: 15, sm: 19 } } }}
    >
        <DialogTitle>Add Gift Cards</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" spacing={1}>
              <Tooltip title="Single Lifetime Access works on either iPhone or Android." describeChild slotProps={tipSlotProps}>
                <Button
                  variant={scope === 'SINGLE' ? 'contained' : 'text'}
                  onClick={() => setScope('SINGLE')}
                >
                  Single
                </Button>
              </Tooltip>
              <Tooltip title="Dual Lifetime Access works on both iPhone and Android." describeChild slotProps={tipSlotProps}>
                <Button
                  variant={scope === 'DUAL' ? 'contained' : 'text'}
                  onClick={() => setScope('DUAL')}
                >
                  Dual
                </Button>
              </Tooltip>
            </Stack>
            {!showPassFields && (
              <>
                <Typography>
                  {`Expires: ${expiryText(view?.expiresAt || null)}`}
                  <FieldTip title="When the gift cards already in this campaign stop being available." />
                </Typography>
                <TextField
                  label={(
                    <span>
                      Number of Gift Cards
                      <FieldTip title="How many gift cards to add to this campaign." />
                    </span>
                  )}
                  type="number"
                  disabled={outOfStock}
                  value={outOfStock ? 0 : count}
                  onChange={(event) => setCount(Number(event.target.value))}
                  inputProps={outOfStock ? { min: 0 } : { min: 1, max: preview.available }}
                  helperText={amountHelp}
                  FormHelperTextProps={outOfStock || overLimit ? { className: 'qc-stock-warning' } : undefined}
                />
              </>
            )}
            {showPassFields && (
              <SendGiftFields
                scope={scope}
                onScope={(next) => setScope(next)}
                showCount
                count={count}
                onCount={(next) => setCount(next)}
                cardCount={count}
                available={preview.available}
                emptyMessage={emptyMessage}
                expireValue={expireValue}
                expireOptions={expireOptions}
                onExpire={(value) => setExpire(value)}
                customDays={customDays}
                onCustomDays={(days) => setCustomDays(days)}
                daysLeft={preview.daysLeft}
                extrasValue={expireValue}
                onExtras={() => undefined}
                extrasCustomDays={customDays}
                onExtrasCustomDays={() => undefined}
                message=""
                onMessage={() => undefined}
                showMessage={false}
                showExtras={false}
                tips={{
                  scope: 'Single covers either iPhone or Android. Dual covers both.',
                  count: 'How many gift cards to add to this campaign.',
                  expire: 'When these gift cards stop being available to request.',
                  customDays: 'How many days until these gift cards expire.',
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => history.push('/u/lifetime-credits')}>Buy More Gift Cards</Button>
          <Button
            variant="contained"
            className={addBlocked ? 'qc-add-unavailable' : undefined}
            disabled={addBlocked}
            onClick={() => save()}
          >
            Add Gift Cards
          </Button>
        </DialogActions>
      </Dialog>
  );

  return {
    singleCount: offer?.single?.available ?? 0,
    dualCount: offer?.dual?.available ?? 0,
    single: offer?.single || null,
    dual: offer?.dual || null,
    total,
    open: openDialog,
    openWith,
    dialog,
  };
}

function QuickLinksGiftBar({
  opener = 0,
  alignEnd = false,
  onAdded,
}: {
  opener?: number;
  alignEnd?: boolean;
  onAdded?: () => void;
}): JSX.Element {
  const offer = useQuickLinksOffer(opener, onAdded);

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent={alignEnd ? 'space-between' : 'flex-start'}
        sx={alignEnd ? { width: '100%' } : undefined}
      >
        <Typography sx={alignEnd ? { fontSize: 20 } : undefined}>{`Gift Cards to Give: ${offer.total}`}</Typography>
        <Button variant="contained" onClick={() => offer.open()}>Add Gift Cards</Button>
      </Stack>
      {offer.dialog}
    </>
  );
}

QuickLinksGiftBar.defaultProps = {
  opener: 0,
  alignEnd: false,
  onAdded: undefined,
};

export default QuickLinksGiftBar;
